/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, Input } from '@angular/core';
import { DND_GRID_ENGINE, DndDragSource, DndManager, GridDndDropTarget } from '@gh/core-ui';
import { arrayify } from '@gh/core-util';
import { concatWith$, map$, first$ } from '@gh/rx/operators';
import {
  DraggingEvent,
  DropTarget,
  GridApi,
  GridDraggingEvent,
  GridOptions,
  RowDragEnterEvent,
} from 'ag-grid-community';
import { getGridDragAndDropService } from '../../grid-api.utils';
import { GRID_EVENT } from '../../grid-public-events';
import { create } from 'lodash';
import { Subscription } from 'rxjs';
import {
  GRID_FEATURE,
  GridFeature,
  GridFeatureInitParams,
  GridFeatureReadyParams,
} from '../feature.interfaces';

interface AgRowDropZoneParams {
  getContainer: () => HTMLElement;
  onDragEnter?: (e: unknown) => void;
  onDragLeave?: (e: unknown) => void;
  onDragging?: (e: unknown) => void;
  onDragStop?: (e: unknown) => void;
  onDragCancel?: (e: unknown) => void;
}

type GridApiWithRowDropZone = GridApi & {
  addRowDropZone?: (params: AgRowDropZoneParams) => void;
  removeRowDropZone?: (params: Pick<AgRowDropZoneParams, 'getContainer'>) => void;
};

/**
 * This grid feature makes grid component suitable for <code>DragSource</code> role in heteragenous DnD operations.
 */
@Directive({
  standalone: false,
  selector: '[ghGridDragSource]',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridDragSourceDirective,
    multi: true,
  }],
  exportAs: 'ghGridDragSource',
})
export class GridDragSourceDirective implements GridFeature {
  @Input('ghGridDragSource') selector: string;

  private gridDnd: { addDropTarget: (t: DropTarget) => void } | undefined;

  private useRowDropZoneApi = false;

  private gridApi: GridApi | undefined;

  private rowDropZoneGetContainers: Array<() => HTMLElement> = [];

  private seenDropTargetRefs = new Set<DropTarget>();

  private lastSyntheticTreeDropHost: HTMLElement | null = null;

  private dragSource: DndDragSource;
  private dropTargetSubscription: Subscription;

  private dragging = false;
  private cachedDropSlots: HTMLElement[] | null = null;
  private cachedContentRows: HTMLElement[] | null = null;
  private domCacheObserver: MutationObserver | null = null;
  private observedTreeRoot: HTMLElement | null = null;

  private readonly onRowDragEnterHandler = (event: RowDragEnterEvent) => {
    this.onRowDragEnter(event);
  };

  private readonly onRowDragEndHandler = () => {
    queueMicrotask(() => this.onRowDragEnd());
  };

  constructor(private dndManager: DndManager) {

  }

  initGrid(params: GridFeatureInitParams): void {
    // nothing to do
  }

  enhanceGridOptions(options: GridOptions): void {
    // nothing to do
  }

  onGridReady(params: GridFeatureReadyParams): void {
    const api = params.api;
    this.gridApi = api;
    const dnd = getGridDragAndDropService(api) ?? (params.sharedBeans as any)?.dragAndDrop
      ?? (params.sharedBeans as any)?.dragAndDropService;
    this.useRowDropZoneApi = typeof (api as GridApi & { addRowDropZone?: (p: unknown) => void })
      .addRowDropZone === 'function';

    if (!this.useRowDropZoneApi && !dnd?.addDropTarget) {
      this.gridApi = void 0;
      return;
    }
    this.gridDnd = dnd;

    // create new drag source
    this.dragSource = this.dndManager.createDragSource(this.selector, DND_GRID_ENGINE);

    // listene for drop targets
    const dndSubscription = this.dndManager.listenDropTargets(this.selector, DND_GRID_ENGINE);
    this.dropTargetSubscription = dndSubscription.targets.pipe(
      first$(),
      concatWith$(dndSubscription.targetAdded.pipe(map$(arrayify))))
      .subscribe((targets: GridDndDropTarget[]) =>
        targets.forEach((target) => this.addDropTargetToGridApi(target.gridDropTarget)));

    api.addEventListener(GRID_EVENT.rowDragEnter, this.onRowDragEnterHandler);
    api.addEventListener(GRID_EVENT.rowDragEnd, this.onRowDragEndHandler);
  }

  onDestroy(): void {
    if (this.dropTargetSubscription) {
      this.dropTargetSubscription.unsubscribe();
    }
    this.teardownRowDropZones();
    if (this.gridApi) {
      this.gridApi.removeEventListener(GRID_EVENT.rowDragEnter, this.onRowDragEnterHandler);
      this.gridApi.removeEventListener(GRID_EVENT.rowDragEnd, this.onRowDragEndHandler);
    }
    this.gridApi = void 0;
    this.useRowDropZoneApi = false;
    this.gridDnd = void 0;
    this.seenDropTargetRefs.clear();
    if (this.dragSource) {
      this.dndManager.removeDragSource(this.dragSource);
    }
  }

  private addDropTargetToGridApi(rawDropTarget: DropTarget): void {
    if (this.seenDropTargetRefs.has(rawDropTarget)) {
      return;
    }
    this.seenDropTargetRefs.add(rawDropTarget);
    const proxied = this.proxyGridDropTarget(rawDropTarget);
    if (this.useRowDropZoneApi) {
      const getContainer = () => (proxied.getContainer as () => HTMLElement)();
      const params: AgRowDropZoneParams = {
        getContainer,
        onDragEnter: (e) => { proxied.onDragEnter && proxied.onDragEnter(e as unknown as GridDraggingEvent); },
        onDragLeave: (e) => { proxied.onDragLeave && proxied.onDragLeave(e as unknown as GridDraggingEvent); },
        onDragging: (e) => { proxied.onDragging && proxied.onDragging(e as unknown as GridDraggingEvent); },
        onDragStop: (e) => { proxied.onDragStop && proxied.onDragStop(e as unknown as GridDraggingEvent); },
        onDragCancel: (e) => {
          (proxied as { onDragCancel?: (p: unknown) => void }).onDragCancel?.(e);
        },
      };
      (this.gridApi as GridApiWithRowDropZone).addRowDropZone!(params);
      this.rowDropZoneGetContainers.push(getContainer);
    } else {
      this.gridDnd?.addDropTarget(proxied);
    }
  }

  private teardownRowDropZones(): void {
    this.clearSyntheticTreeGridHover();
    this.invalidateDomCache();
    this.domCacheObserver?.disconnect();
    this.domCacheObserver = null;
    this.observedTreeRoot = null;
    if (this.useRowDropZoneApi && this.gridApi) {
      const api = this.gridApi as GridApiWithRowDropZone;
      if (api.removeRowDropZone) {
        for (const getContainer of this.rowDropZoneGetContainers) {
          api.removeRowDropZone.call(api, { getContainer });
        }
      }
    }
    this.rowDropZoneGetContainers = [];
  }

  private onRowDragEnter(event: RowDragEnterEvent): void {
    if (!this.dragging) {
      this.dragSource.startDrag(event.node.data);
    }

    this.dragging = true;
  }

  private onRowDragEnd(): void {
    this.dragging = false;
    this.dragSource.stopDrag();
  }

  /**
   * This method proxy {@link DropTarget}.
   * We have to proxy drop target to delegate onRowDragEnd event.
   * Grid does not fire onRowDragEnd event if it has occurred outside of grid.
   */
  private proxyGridDropTarget(dropTarget: DropTarget): DropTarget {
    const treeRoot = () => dropTarget.getContainer?.() as HTMLElement | undefined;
    return create(dropTarget, {
      onDragEnter: (e: any) => {
        if (dropTarget.onDragEnter) {
          dropTarget.onDragEnter(e);
        }
      },
      onDragging: (e: any) => {
        const root = treeRoot();
        if (root) {
          this.syntheticTreePointerMoveWhileGridRowDrag(root, e);
        }
        if (dropTarget.onDragging) {
          dropTarget.onDragging(e);
        }
      },
      onDragLeave: (e: any) => {
        this.clearSyntheticTreeGridHover();
        if (dropTarget.onDragLeave) {
          dropTarget.onDragLeave(e);
        }
      },
      onDragStop: (event: DraggingEvent) => {
        const root = treeRoot();
        this.clearSyntheticTreeGridHover();
        if (root) {
          this.syntheticTreeMouseUpForGridRowDrop(root, event);
        }
        if (dropTarget.onDragStop) {
          dropTarget.onDragStop(event as any);
        }
        queueMicrotask(() => this.onRowDragEnd());
      },
    });
  }

  private clearSyntheticTreeGridHover(): void {
    if (this.lastSyntheticTreeDropHost) {
      this.lastSyntheticTreeDropHost.dispatchEvent(
        new MouseEvent('mouseleave', { bubbles: true, cancelable: true, view: window, button: 0, buttons: 0 }),
      );
      this.lastSyntheticTreeDropHost = null;
    }
  }

  private syntheticTreePointerMoveWhileGridRowDrag(treeRoot: HTMLElement, agEvent: unknown): void {
    const me = this.tryGetMouseEventFromAgDropCallback(agEvent);
    if (!me) {
      return;
    }
    const { clientX, clientY } = me;
    if (!this.isPointInsideElement(treeRoot, clientX, clientY)) {
      this.clearSyntheticTreeGridHover();
      return;
    }
    const host = this.findTreeGridDropHostUnderPoint(treeRoot, clientX, clientY);
    if (!host) {
      this.clearSyntheticTreeGridHover();
      return;
    }
    if (host === this.lastSyntheticTreeDropHost) {
      host.dispatchEvent(
        this.buildMouseEvent('mousemove', me),
      );
      return;
    }
    this.clearSyntheticTreeGridHover();
    host.dispatchEvent(
      this.buildMouseEvent('mouseenter', me),
    );
    host.dispatchEvent(
      this.buildMouseEvent('mousemove', me),
    );
    this.lastSyntheticTreeDropHost = host;
  }

  private syntheticTreeMouseUpForGridRowDrop(treeRoot: HTMLElement, agStopEvent: unknown): void {
    const me = this.tryGetMouseEventFromAgDropCallback(agStopEvent);
    if (!me) {
      return;
    }
    const { clientX, clientY } = me;
    if (!this.isPointInsideElement(treeRoot, clientX, clientY)) {
      return;
    }
    const host = this.findTreeGridDropHostUnderPoint(treeRoot, clientX, clientY);
    if (host) {
      host.dispatchEvent(this.buildPointerEvent('pointerup', me));
      host.dispatchEvent(this.buildMouseEvent('mouseup', me));
    }
  }

  private static readonly TREE_DROP_SLOT_VERT_PAD = 6;

  private ensureDomCacheObserver(treeRoot: HTMLElement): void {
    if (this.observedTreeRoot === treeRoot && this.domCacheObserver) {
      return;
    }
    this.domCacheObserver?.disconnect();
    this.invalidateDomCache();
    this.observedTreeRoot = treeRoot;
    this.domCacheObserver = new MutationObserver(() => this.invalidateDomCache());
    this.domCacheObserver.observe(treeRoot, { childList: true, subtree: true });
  }

  private invalidateDomCache(): void {
    this.cachedDropSlots = null;
    this.cachedContentRows = null;
  }

  private getDropSlots(treeRoot: HTMLElement): HTMLElement[] {
    if (!this.cachedDropSlots) {
      this.cachedDropSlots = Array.from(treeRoot.querySelectorAll<HTMLElement>('.node-drop-slot'));
    }
    return this.cachedDropSlots;
  }

  private getContentRows(treeRoot: HTMLElement): HTMLElement[] {
    if (!this.cachedContentRows) {
      this.cachedContentRows = Array.from(treeRoot.querySelectorAll<HTMLElement>('.node-content-wrapper'));
    }
    return this.cachedContentRows;
  }


  private findTreeGridDropHostUnderPoint(treeRoot: HTMLElement, clientX: number, clientY: number): HTMLElement | null {
    this.ensureDomCacheObserver(treeRoot);
    const fromTightSlot = this.findDropSlotByTightRect(treeRoot, clientX, clientY);
    if (fromTightSlot) {
      return fromTightSlot;
    }
    const fromInflatedSlot = this.findDropSlotByInflatedHit(treeRoot, clientX, clientY);
    if (fromInflatedSlot) {
      return fromInflatedSlot;
    }
    const fromTightRow = this.findTightNodeContentRow(treeRoot, clientX, clientY);
    if (fromTightRow) {
      return fromTightRow;
    }

    const stack = document.elementsFromPoint(clientX, clientY);
    let iSlot = -1;
    let firstSlot: HTMLElement | null = null;
    let iRow = -1;
    let firstRow: HTMLElement | null = null;
    for (let i = 0; i < stack.length; i++) {
      const node = stack[i];
      if (!(node instanceof HTMLElement)) {
        continue;
      }
      if (this.isAgDndOverlayElement(node)) {
        continue;
      }
      if (!treeRoot.contains(node)) {
        continue;
      }
      if (iSlot < 0) {
        const s = node.classList?.contains('node-drop-slot')
          ? node
          : node.closest<HTMLElement>('.node-drop-slot');
        if (s) {
          iSlot = i;
          firstSlot = s;
        }
      }
      if (iRow < 0) {
        const r = node.closest<HTMLElement>('.node-content-wrapper');
        if (r) {
          iRow = i;
          firstRow = r;
        }
      }
    }

    if (!firstRow && !firstSlot) {
      return null;
    }
    if (!firstRow) {
      return firstSlot;
    }
    if (!firstSlot) {
      return firstRow;
    }
    if (iSlot < iRow) {
      return firstSlot;
    }
    if (iRow < iSlot && this.isPointInsidePaddedElement(
      firstSlot, clientX, clientY, GridDragSourceDirective.TREE_DROP_SLOT_VERT_PAD, 0)) {
      return firstSlot;
    }
    return firstRow;
  }

  private findDropSlotByTightRect(treeRoot: HTMLElement, x: number, y: number): HTMLElement | null {
    const nodes = this.getDropSlots(treeRoot);
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      if (this.isAgDndOverlayElement(el) || !treeRoot.contains(el)) {
        continue;
      }
      if (this.isPointInsideElement(el, x, y)) {
        return el;
      }
    }
    return null;
  }

  private findTightNodeContentRow(treeRoot: HTMLElement, x: number, y: number): HTMLElement | null {
    const nodes = this.getContentRows(treeRoot);
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      if (this.isAgDndOverlayElement(el) || !treeRoot.contains(el)) {
        continue;
      }
      if (this.isPointInsideElement(el, x, y)) {
        return el;
      }
    }
    return null;
  }

  private findDropSlotByInflatedHit(treeRoot: HTMLElement, x: number, y: number): HTMLElement | null {
    const vPad = GridDragSourceDirective.TREE_DROP_SLOT_VERT_PAD;
    const nodes = this.getDropSlots(treeRoot);
    const candidates: HTMLElement[] = [];
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      if (this.isAgDndOverlayElement(el) || !treeRoot.contains(el)) {
        continue;
      }
      if (this.isPointInsidePaddedElement(el, x, y, vPad, 0)) {
        candidates.push(el);
      }
    }
    if (candidates.length === 0) {
      return null;
    }
    if (candidates.length === 1) {
      return candidates[0];
    }
    return candidates.reduce((best, el) => {
      const r = el.getBoundingClientRect();
      const lineY = r.height > 0 ? r.top + r.height / 2 : r.top;
      const b = best.getBoundingClientRect();
      const bLineY = b.height > 0 ? b.top + b.height / 2 : b.top;
      return Math.abs(y - lineY) < Math.abs(y - bLineY) ? el : best;
    });
  }

  private isPointInsidePaddedElement(
    el: HTMLElement,
    x: number,
    y: number,
    padY: number,
    padX: number,
  ): boolean {
    const r = el.getBoundingClientRect();
    if (r.width <= 0 && r.height <= 0) {
      return false;
    }
    return x >= r.left - padX
      && x <= r.right + padX
      && y >= r.top - padY
      && y <= r.bottom + padY;
  }

  private isAgDndOverlayElement(el: HTMLElement): boolean {
    return el.classList.contains('ag-dnd-ghost') || !!el.closest?.('.ag-dnd-ghost');
  }

  private isPointInsideElement(el: HTMLElement, x: number, y: number): boolean {
    const r = el.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  private buildPointerEvent(
    type: 'pointerup' | 'pointermove',
    source: MouseEvent | PointerEvent,
  ): PointerEvent {
    const p = source as PointerEvent;
    return new PointerEvent(type, {
      bubbles: true,
      cancelable: true,
      view: (source as MouseEvent).view ?? window,
      clientX: source.clientX,
      clientY: source.clientY,
      button: (source as MouseEvent).button ?? 0,
      buttons: type === 'pointerup' ? 0 : (source as MouseEvent).buttons ?? 0,
      pointerId: p.pointerId != null ? p.pointerId : 1,
      pointerType: p.pointerType ? (p.pointerType as 'mouse' | 'pen' | 'touch') : 'mouse',
    });
  }

  private buildMouseEvent(
    type: 'mousemove' | 'mouseup' | 'mouseenter' | 'mouseleave',
    source: MouseEvent | PointerEvent,
  ): MouseEvent {
    const view = (source as MouseEvent).view ?? window;
    const buttons = type === 'mouseup' || type === 'mouseleave' ? 0 : (source.buttons ?? 0);
    return new MouseEvent(type, {
      bubbles: true,
      cancelable: true,
      view,
      detail: (source as MouseEvent).detail ?? 0,
      screenX: source.screenX,
      screenY: source.screenY,
      clientX: source.clientX,
      clientY: source.clientY,
      ctrlKey: source.ctrlKey,
      shiftKey: source.shiftKey,
      altKey: source.altKey,
      metaKey: source.metaKey,
      button: (source as MouseEvent).button ?? 0,
      buttons,
      relatedTarget: null,
    });
  }

  private tryGetMouseEventFromAgDropCallback(agEvent: unknown): MouseEvent | undefined {
    if (!agEvent || typeof agEvent !== 'object') {
      return undefined;
    }
    const o = agEvent as { event?: unknown };
    if (o.event instanceof MouseEvent) {
      return o.event;
    }
    if (o.event && typeof o.event === 'object' && (o.event as PointerEvent).clientX != null) {
      const p = o.event as PointerEvent;
      return p as unknown as MouseEvent;
    }
    return undefined;
  }
}
